import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Github } from "lucide-react";
import { useAdoptGithubConnection } from "@/hooks/git";
import { useStartMcpOAuth } from "@/hooks/tools";

// ─── Not Connected View ───────────────────────────────────────────────────────

export function NotConnectedView({ error }: { error?: Error | null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // One GitHub OAuth connection serves both the Git section and the MCP page —
  // a classic OAuth App has a single registered callback URL, so there is only
  // ever one flow, owned by the MCP gateway.
  const adopt = useAdoptGithubConnection();
  const startOAuth = useStartMcpOAuth();

  // Connecting is one click, and only redirects when it has to: adopt the token the
  // gateway already holds, and fall back to the OAuth flow on 401 (nothing to adopt).
  // Reading no longer adopts on its own — that is what made Disconnect Git impossible.
  const connect = () =>
    adopt.mutate(undefined, {
      onError: (err) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 401) startOAuth.mutate({ provider: "github", surface: "git" });
      },
    });

  useEffect(() => {
    if (searchParams.get("github_connected") !== "true") return;
    setSearchParams((prev) => { prev.delete("github_connected"); return prev; });
    connect();
  }, []);

  const busy = adopt.isPending || startOAuth.isPending;
  // A 401 is the ordinary "nothing to adopt" answer — it starts the OAuth flow rather
  // than reporting a failure. Everything else is a real error and says so.
  const adoptFailed =
    adopt.isError &&
    (adopt.error as { response?: { status?: number } })?.response?.status !== 401;

  return (
    <div className="flex h-full flex-col items-center justify-center py-20 text-center">
      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-card">
        <Github className="h-7 w-7 text-foreground" />
      </div>
      <h2 className="mb-2 text-lg font-semibold">Connect GitHub</h2>
      <p className="mb-8 max-w-sm text-sm text-muted-foreground leading-relaxed">
        Authorize GitHub to browse repos, inspect pull requests, and run pipeline checks. If you
        already connected GitHub under MCP, this takes you straight through.
      </p>
      {/* GitHub IS connected under MCP, but adopting that token here failed. Without this the
          screen said "not connected" and sent them round the OAuth loop again, which cannot fix
          a server-side failure. */}
      {error && (
        <p className="mb-6 max-w-sm rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          Could not link your GitHub connection to Git: {error.message}
        </p>
      )}
      <button
        type="button"
        onClick={connect}
        disabled={busy}
        className="studio-button studio-button-primary gap-2 px-5 py-2.5"
      >
        <Github className="h-4 w-4" />
        {startOAuth.isPending ? "Redirecting…" : adopt.isPending ? "Connecting…" : "Connect GitHub"}
      </button>
      {(adoptFailed || startOAuth.isError) && (
        <p className="mt-4 max-w-sm text-sm text-destructive">
          Could not connect GitHub. Please try again.
        </p>
      )}
    </div>
  );
}
